/*
 * The Chief's audit feed on the web (docs/specs/chief-of-office.md §8.3). Pure helpers: what a row says,
 * who may undo it, the Bridge tile's count and the task-timeline line. The row shape is the hub's
 * ChiefActionDTO (GET /api/chief/actions); core does not export it, so it is mirrored here.
 */

export type ChiefActionDTO = {
  id: string;
  at: number;
  chiefId: string;
  projectId: string | null;
  tool: string;
  subjectType: string | null;
  subjectId: string | null;
  input: unknown;
  reason: string;
  trigger: string;
  reversible: boolean;
  irreversibleWhy: string | null;
  undoneAt: number | null;
  undoneBy: string | null;
  changedSince: Record<string, { expected: unknown; actual: unknown }> | null;
  runId: string | null;
  costUsd: number;
};

export type ChiefActionFilters = { tool?: string; projectId?: string; undone?: "true" | "false"; trigger?: string };

/** What each act tool reads as in the feed. */
export const CHIEF_TOOL_LABEL: Record<string, string> = {
  chief_message: "Sent a message",
  chief_steer_run: "Steered a run",
  chief_set_priority: "Changed a priority",
  chief_assign_builder: "Named a builder",
  chief_file_task: "Filed a task",
  chief_pause_schedule: "Paused a schedule",
  chief_resume_schedule: "Resumed a schedule",
};

export const CHIEF_TRIGGER_KINDS = ["chat", "scan", "manual"] as const;

export function chiefToolLabel(tool: string): string {
  return CHIEF_TOOL_LABEL[tool] ?? tool.replace(/^chief_/, "").replaceAll("_", " ");
}

/** Undo is for members and admins (§8.3); viewers read the feed only. */
export function canUndoChiefAction(access: string | undefined): boolean {
  return access === "admin" || access === "member";
}

export type ChiefRowState = "undone" | "changed" | "irreversible" | "undoable";

export function chiefRowState(a: ChiefActionDTO): ChiefRowState {
  if (a.undoneAt !== null) return "undone";
  if (!a.reversible) return "irreversible";
  if (a.changedSince) return "changed";
  return "undoable";
}

/** "a sent message cannot be recalled" for an irreversible row; the changed-fields note for "changed since, not undone". */
export function chiefRowNote(a: ChiefActionDTO): string | null {
  switch (chiefRowState(a)) {
    case "irreversible":
      return a.irreversibleWhy ?? "This action cannot be undone.";
    case "changed":
      return `Changed since, not undone: ${Object.keys(a.changedSince ?? {}).join(", ")}`;
    default:
      return null;
  }
}

/** Where a trigger leads: a scan finding or the chat thread. `manual` and unknown values link nowhere. */
export function chiefTriggerView(trigger: string): { label: string; href: string | null } {
  const [kind, ...rest] = trigger.split(":");
  if (kind === "chat") return { label: "chat message", href: "/conversations" };
  if (kind === "scan") {
    // scan:<scanName>:<scanName>:<subjectId>; the finding's subject is the page worth opening.
    const [scan, ...key] = rest;
    const subject = key.at(-1) ?? "";
    const type = subject.startsWith("tsk_") ? "task" : subject.startsWith("run_") ? "run" : null;
    return { label: `scan ${(scan ?? "").replaceAll("_", " ")}`.trim(), href: subjectHref(type, subject) };
  }
  return { label: kind === "manual" ? "manual" : trigger, href: null };
}

/** The page a subject lives on, when there is one. */
export function subjectHref(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  if (type === "task") return `/tasks/${id}`;
  if (type === "run") return `/runs/${id}`;
  if (type === "actor") return `/team/${id}`;
  return null;
}

/** Start of the viewer's local day. */
export function startOfDay(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** The Bridge tile: actions taken today and how many of those were undone. */
export function chiefToday(actions: readonly ChiefActionDTO[], now: number): { actions: number; undone: number } {
  const from = startOfDay(now);
  const today = actions.filter((a) => a.at >= from);
  return { actions: today.length, undone: today.filter((a) => a.undoneAt !== null).length };
}

export const CHIEF_PAGE = 500; // the hub's limit cap
/** Pages stop here so a runaway feed cannot hold the Bridge up; 25 pages is 5000 acts in one day. */
const CHIEF_MAX_PAGES = 25;

/**
 * Every action at or after `from`, newest first, read page by page with `before`. A single limit-200 read
 * stuck the Bridge count at 200 on busy days. `any` is true when the feed has a row at all, however old.
 */
export async function chiefActionsSince(
  from: number,
  fetchPage: (before: number | undefined) => Promise<ChiefActionDTO[]>,
): Promise<{ actions: ChiefActionDTO[]; any: boolean }> {
  const actions: ChiefActionDTO[] = [];
  const seen = new Set<string>();
  let any = false;
  let before: number | undefined;
  for (let page = 0; page < CHIEF_MAX_PAGES; page++) {
    const rows = await fetchPage(before);
    if (page === 0) any = rows.length > 0;
    const fresh = rows.filter((a) => !seen.has(a.id));
    for (const a of fresh) {
      seen.add(a.id);
      if (a.at >= from) actions.push(a);
    }
    const oldest = rows.at(-1);
    // `before` is strict (at < before), so +1 re-reads the oldest millisecond: rows tied on `at` across a
    // page edge are kept, and `seen` drops the repeats. A page with nothing new cannot advance: stop.
    if (rows.length < CHIEF_PAGE || !oldest || oldest.at < from || fresh.length === 0) break;
    before = oldest.at + 1;
  }
  return { actions, any };
}

export function chiefTileText(t: { actions: number; undone: number }): string {
  return `Chief: ${t.actions} ${t.actions === 1 ? "action" : "actions"} today, ${t.undone} undone`;
}

/** An action on a task, as the task timeline shows it: never a bare name (§8.3). */
export function chiefTimelineLine(a: Pick<ChiefActionDTO, "tool" | "reason">): string {
  return `Chief (agent) · ${chiefToolLabel(a.tool)} · ${a.reason}`;
}

export function chiefActionsForTask(actions: readonly ChiefActionDTO[], taskId: string): ChiefActionDTO[] {
  return actions.filter((a) => a.subjectType === "task" && a.subjectId === taskId);
}

export function chiefQuery(f: ChiefActionFilters & { limit?: number; before?: number }): Record<string, string | undefined> {
  return { tool: f.tool || undefined, projectId: f.projectId || undefined, undone: f.undone, trigger: f.trigger || undefined, limit: f.limit ? String(f.limit) : undefined, before: f.before !== undefined ? String(f.before) : undefined };
}
